import fp from "lodash";
import moment from "moment";
import qs from "qs";

import { FeatureManager, RefreshInProgress, URLS } from "globalConstants";
import { history, authHeader, sleep, axios, store } from "helpers";
import {
	handleResponse,
	handleError,
	fusionAuthService,
	userService,
} from "services";
import { userActions } from "store";

const FOUR_MINUTES_IN_SECONDS = 4 * 60;

export function isSessionExpired() {
	const now = moment();
	const lastAction = localStorage.getItem("lastUserAction");
	const user = JSON.parse(localStorage.getItem("user"));
	let isExpired = true;
	if (user?.expires_in) {
		const logoutAt = moment(lastAction).add(user?.expires_in, "seconds");
		isExpired = logoutAt.isBefore(now);
	}
	const currentValue = store.getState().user.isSessionExpired;
	if (isExpired !== currentValue) {
		store.dispatch(userActions.setIsSessionExpired(isExpired));
	}
	return isExpired;
}

function refreshTokenFn(getRefreshedToken, refreshTokenValue) {
	const currentUser = localStorage.getItem("user");

	if (!currentUser) {
		history.replace(URLS.logout);
		return Promise.reject("User unauthorized");
	}

	return getRefreshedToken(refreshTokenValue)
		.then(res => {
			// setUserActionDate();

			const dateOfExpiration = new Date();
			dateOfExpiration.setSeconds(
				dateOfExpiration.getSeconds() + res.data.expires_in,
			);

			localStorage.setItem(
				"user",
				JSON.stringify({
					...res.data,
					expires_date: dateOfExpiration,
					isRefresh: true,
				}),
			);

			return Promise.resolve(res.data);
		})
		.catch(err => {
			history.replace(URLS.logout);
			return Promise.reject(err);
		})
		.finally(() => {
			setTimeout(() => {
				localStorage.setItem(RefreshInProgress, "false");
			}, 1000);
		});
}

export const refreshToken = refreshTokenValue =>
	FeatureManager.isEnabled("fusionAuth")
		? refreshTokenFn(
				fusionAuthService.getRefreshedTokens,
				refreshTokenValue,
		  )
		: refreshTokenFn(userService.getRefreshedTokens, refreshTokenValue);

export async function axiosWithAuth(options, disableTokenRefresh = false) {
	if (!options) {
		options = {};
	}

	options.headers = { ...authHeader("application/json"), ...options.headers };

	let tryCount = 3;

	while (tryCount > 0) {
		const refreshInProgress = localStorage.getItem(RefreshInProgress);

		if (!refreshInProgress || refreshInProgress === "false") {
			break;
		}

		await sleep(1000);
		tryCount--;
	}

	let user = JSON.parse(localStorage.getItem("user"));
	if (!user) {
		history.replace(URLS.logout);
		return Promise.reject("User unauthorized");
	}

	const expiresInWithOffset = new Date(user.expires_date);

	expiresInWithOffset.setSeconds(
		expiresInWithOffset.getSeconds() - FOUR_MINUTES_IN_SECONDS,
	);

	const now = new Date();

	if (now > new Date(user.expires_date)) {
		localStorage.removeItem("user");
		history.push(URLS.login);
		return Promise.reject("User unauthorized");
	}

	if (now >= expiresInWithOffset && !disableTokenRefresh) {
		try {
			localStorage.setItem(RefreshInProgress, "true");
			let tokenData = await refreshToken(user?.refresh_token);

			options.headers[
				"Authorization"
			] = `Bearer ${tokenData.access_token}`;
		} catch {
			localStorage.setItem(RefreshInProgress, "false");
			history.replace(URLS.logout);
			return Promise.reject("User unauthorized");
		}
	}

	if (options.arrayFormat) {
		options.paramsSerializer = arrayParamsSerializer;
	}

	return axios(options)
		.then(handleResponse)
		.catch(error => handleError(error));
}

function arrayParamsSerializer(params) {
	return qs.stringify(params, { arrayFormat: "repeat" });
}

export const setExpiresDate = data => {
	const dateOfExpiration = new Date();

	dateOfExpiration.setSeconds(
		dateOfExpiration.getSeconds() + data.expires_in,
	);

	localStorage.setItem(
		"user",
		JSON.stringify({
			...data,
			expires_date: dateOfExpiration,
		}),
	);
};

export const checkTokenExpiration = () => {
	const user = localStorage.getItem("user");
	const data = user && JSON.parse(user);

	const isExpired =
		fp.isEmpty(data) || new Date() >= new Date(data?.expires_date);

	return isExpired;
};
